// BusinessAuthenticationCoordinator.js
// FINAL FIXED VERSION - Combines your existing static methods with instance methods
// This fixes all the "is not a function" errors

import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';

export class BusinessAuthenticationCoordinator {
  static API_BASE_URL = 'https://api.jobeng.ai';
  
  // ADD CONSTRUCTOR FOR INSTANCE METHODS
  constructor() {
    this.isInitialized = false;
    this.biometricAvailable = false;
    this.biometricTypes = [];
    this.hasSavedCredentials = false;
    this.currentUser = null;
  }

  // ===========================================
  // INSTANCE METHODS (FIXES THE ERRORS)
  // ===========================================

  // Instance method: checkBiometricAvailability (FIXES THE ERROR)
  async checkBiometricAvailability() {
    try {
      console.log('🔍 Checking biometric availability (instance method)...');
      
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();
      
      let biometricType = 'Biometric';
      if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
        biometricType = 'Face ID';
      } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
        biometricType = 'Fingerprint';
      } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
        biometricType = 'Iris';
      }

      this.biometricAvailable = hasHardware && isEnrolled;
      this.biometricTypes = supportedTypes;

      return {
        available: hasHardware && isEnrolled,
        type: biometricType,
        hasHardware,
        isEnrolled,
        supportedTypes,
      };
    } catch (error) {
      console.error('❌ Error checking biometric availability:', error);
      this.biometricAvailable = false;
      return {
        available: false,
        type: 'Biometric',
        hasHardware: false,
        isEnrolled: false,
        supportedTypes: [],
      };
    }
  }

  // Instance method: hasSavedBiometricCredentials (FIXES THE ERROR)
  async hasSavedBiometricCredentials() {
    try {
      console.log('🔍 Checking saved biometric credentials (instance method)...');
      
      const savedEmail = await AsyncStorage.getItem(BusinessAuthenticationCoordinator.STORAGE_KEYS.BIOMETRIC_EMAIL);
      const savedPassword = await AsyncStorage.getItem(BusinessAuthenticationCoordinator.STORAGE_KEYS.BIOMETRIC_PASSWORD);
      
      const hasCredentials = !!(savedEmail && savedPassword);
      this.hasSavedCredentials = hasCredentials;
      
      return hasCredentials;
    } catch (error) {
      console.error('❌ Error checking saved credentials:', error);
      return false;
    }
  }

  // Instance method: performLogin (FIXES THE ERROR + EMAIL VALIDATION)
  async performLogin(credentials, loginType = 'standard') {
    try {
      console.log('🔐 Attempting login (instance method)...');
      
      // SAFE EMAIL VALIDATION - This fixes the email.toLowerCase error
      let email = '';
      try {
        if (credentials && credentials.email !== null && credentials.email !== undefined) {
          if (typeof credentials.email === 'string') {
            email = credentials.email.trim().toLowerCase();
          } else if (typeof credentials.email === 'number') {
            email = credentials.email.toString().trim().toLowerCase();
          } else if (typeof credentials.email === 'object') {
            // Handle object case safely - this was causing the error
            console.log('⚠️ Email is an object:', credentials.email);
            email = String(credentials.email).trim().toLowerCase();
          } else {
            email = String(credentials.email).trim().toLowerCase();
          }
        }
      } catch (emailError) {
        console.error('❌ Email processing error:', emailError);
        return { 
          success: false, 
          message: 'Invalid email format'
        };
      }

      // Validate email
      if (!email || email.length === 0) {
        return { 
          success: false, 
          message: 'Email is required'
        };
      }

      if (!BusinessAuthenticationCoordinator.validateEmail(email)) {
        return { 
          success: false, 
          message: 'Please enter a valid email address'
        };
      }

      // SAFE PASSWORD VALIDATION
      let password = '';
      try {
        if (credentials && credentials.password !== null && credentials.password !== undefined) {
          password = String(credentials.password).trim();
        }
      } catch (passwordError) {
        console.error('❌ Password processing error:', passwordError);
        return { 
          success: false, 
          message: 'Invalid password format'
        };
      }

      if (!password || password.length === 0) {
        return { 
          success: false, 
          message: 'Password is required'
        };
      }

      if (!BusinessAuthenticationCoordinator.validatePassword(password)) {
        return { 
          success: false, 
          message: 'Password must be at least 6 characters long'
        };
      }

      // Create clean credentials object
      const cleanCredentials = {
        email: email,
        password: password,
        ...credentials // Include any other properties
      };

      // Use the static method for actual login
      const result = await BusinessAuthenticationCoordinator.performLogin(cleanCredentials, loginType);
      
      if (result.success) {
        this.currentUser = result.user;
      }
      
      return result;

    } catch (error) {
      console.error('❌ Login error (instance method):', error);
      return {
        success: false,
        message: 'An unexpected error occurred during login'
      };
    }
  }

  // Instance method: initialize
  async initialize() {
    try {
      console.log('🔐 Initializing BusinessAuthenticationCoordinator (instance)...');
      
      await this.checkBiometricAvailability();
      await this.hasSavedBiometricCredentials();
      
      this.isInitialized = true;
      
      return { 
        success: true, 
        biometricAvailable: this.biometricAvailable,
        biometricTypes: this.biometricTypes,
        hasSavedCredentials: this.hasSavedCredentials
      };
    } catch (error) {
      console.error('❌ Initialization failed:', error);
      return { 
        success: false, 
        error: error.message,
        biometricAvailable: false,
        hasSavedCredentials: false
      };
    }
  }

  // Instance method: performBiometricLogin
  async performBiometricLogin() {
    try {
      const biometricInfo = await this.checkBiometricAvailability();
      
      if (!biometricInfo.available) {
        throw new Error('Biometric authentication not available');
      }

      // Check for saved credentials
      const savedEmail = await AsyncStorage.getItem(BusinessAuthenticationCoordinator.STORAGE_KEYS.BIOMETRIC_EMAIL);
      const savedPassword = await AsyncStorage.getItem(BusinessAuthenticationCoordinator.STORAGE_KEYS.BIOMETRIC_PASSWORD);
      
      if (!savedEmail || !savedPassword) {
        throw new Error('No saved credentials found for biometric login');
      }

      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: `Login with ${biometricInfo.type}`,
        fallbackLabel: 'Enter your password',
        disableDeviceFallback: false,
      });

      if (result.success) {
        // Perform login with saved credentials using instance method
        const loginResult = await this.performLogin({
          email: savedEmail,
          password: savedPassword,
        }, 'biometric');

        return loginResult;
      } else {
        throw new Error('Biometric authentication failed');
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      return {
        success: false,
        message: error.message || 'Biometric login failed',
      };
    }
  }

  // ===========================================
  // YOUR EXISTING STATIC METHODS (UNCHANGED)
  // ===========================================
  
  // Shared authentication state keys
  static STORAGE_KEYS = {
    AUTH_TOKEN: 'business_auth_token',
    REFRESH_TOKEN: 'business_refresh_token',
    USER_DATA: 'business_user_data',
    COMPANY_DATA: 'business_company_data',
    BIOMETRIC_EMAIL: 'biometric_email_business',
    BIOMETRIC_PASSWORD: 'biometric_password_business',
    BIOMETRIC_USER_TYPE: 'biometric_user_type_business',
    SAVED_EMAIL: 'saved_business_email',
    REMEMBER_LOGIN: 'remember_business_login',
    SELECTED_COMPANY: 'selected_company',
    RECENT_LOGINS: 'recent_business_logins',
    LOGIN_METHOD: 'preferred_login_method',
    SELECTED_ROLE: 'selected_user_role',
  };

  // Shared validation methods
  static validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static validatePassword(password) {
    return password && password.length >= 6;
  }

  static validatePhone(phone) {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  }

  static validateUsername(username) {
    return username && username.length >= 3 && /^[a-zA-Z0-9_]+$/.test(username);
  }

  // Shared API methods
  static async makeAuthRequest(endpoint, data, method = 'POST') {
    try {
      const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const responseText = await response.text();
      console.log(`${method} ${endpoint} response status:`, response.status);
      console.log(`Raw ${endpoint} response:`, responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response format from server');
      }

      return {
        success: response.ok,
        status: response.status,
        data: responseData,
        rawResponse: responseText,
      };
    } catch (error) {
      console.error(`API request error for ${endpoint}:`, error);
      throw error;
    }
  }

  // Shared login method
  static async performLogin(credentials, loginType = 'standard') {
    try {
      const loginData = {
        ...credentials,
        user_type: 'business',
        login_type: loginType,
        device_info: {
          platform: 'mobile',
          app_version: '1.0.0',
        }
      };

      const response = await this.makeAuthRequest('/api/auth/login', loginData);

      if (response.success) {
        // Parse response data (handle nested structure)
        let userData = null;
        let token = null;

        if (response.data.data && response.data.data.data) {
          userData = response.data.data.data.user || response.data.data.user;
          token = response.data.data.data.token || response.data.data.token;
        } else if (response.data.data) {
          userData = response.data.data.user;
          token = response.data.data.token;
        } else {
          userData = response.data.user;
          token = response.data.token;
        }

        if (!userData) {
          throw new Error('Invalid response format: user data not found');
        }

        // Ensure user type is business
        userData.user_type = 'business';

        // Store authentication data
        if (token) {
          await AsyncStorage.setItem(this.STORAGE_KEYS.AUTH_TOKEN, token);
        }

        if (response.data.refresh_token) {
          await AsyncStorage.setItem(this.STORAGE_KEYS.REFRESH_TOKEN, response.data.refresh_token);
        }

        await AsyncStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(userData));

        return {
          success: true,
          user: userData,
          token: token,
          message: 'Login successful',
        };
      } else {
        let errorMessage = 'Login failed. Please try again.';
        
        if (response.data && response.data.message) {
          errorMessage = response.data.message;
        } else if (response.status === 401) {
          errorMessage = 'Invalid credentials. Please check your email and password.';
        } else if (response.status === 422) {
          errorMessage = 'Please check your input data.';
        }

        return {
          success: false,
          message: errorMessage,
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      
      let userMessage = 'Login failed. Please try again.';
      
      if (error.message.includes('Network request failed')) {
        userMessage = 'Network error. Please check your internet connection.';
      } else if (error.message) {
        userMessage = error.message;
      }

      return {
        success: false,
        message: userMessage,
      };
    }
  }

  // Shared signup method
  static async performSignup(signupData) {
    try {
      const data = {
        ...signupData,
        user_type: 'business',
      };

      const response = await this.makeAuthRequest('/api/auth/register', data);

      if (response.success) {
        // Parse response data (handle nested structure)
        let userData = null;
        let token = null;

        if (response.data.data && response.data.data.data) {
          userData = response.data.data.data.user || response.data.data.user;
          token = response.data.data.data.token || response.data.data.token;
        } else if (response.data.data) {
          userData = response.data.data.user;
          token = response.data.data.token;
        } else {
          userData = response.data.user;
          token = response.data.token;
        }

        if (!userData) {
          // If no user data returned, create basic user object
          userData = {
            email: signupData.email,
            full_name: signupData.full_name,
            company_name: signupData.company_name,
            industry: signupData.industry,
            company_size: signupData.company_size,
            user_type: 'business'
          };
        }

        // Ensure user type is business
        userData.user_type = 'business';

        // Store authentication data
        if (token) {
          await AsyncStorage.setItem(this.STORAGE_KEYS.AUTH_TOKEN, token);
        }

        if (response.data.refresh_token) {
          await AsyncStorage.setItem(this.STORAGE_KEYS.REFRESH_TOKEN, response.data.refresh_token);
        }

        await AsyncStorage.setItem(this.STORAGE_KEYS.USER_DATA, JSON.stringify(userData));

        return {
          success: true,
          user: userData,
          token: token,
          message: 'Account created successfully',
        };
      } else {
        let errorMessage = 'Signup failed. Please try again.';
        
        if (response.data && response.data.message) {
          errorMessage = response.data.message;
        } else if (response.data && response.data.details) {
          // Handle validation errors with details
          const errors = response.data.details.map(detail => detail.msg);
          errorMessage = errors.join('\n');
        } else if (response.data && response.data.errors) {
          const errors = Object.values(response.data.errors).flat();
          errorMessage = errors.join('\n');
        } else if (response.status === 422) {
          errorMessage = 'Please check your input data.';
        } else if (response.status === 409) {
          errorMessage = 'An account with this email already exists.';
        }

        return {
          success: false,
          message: errorMessage,
        };
      }
    } catch (error) {
      console.error('Signup error:', error);
      
      let userMessage = 'Signup failed. Please try again.';
      
      if (error.message.includes('Network request failed')) {
        userMessage = 'Network error. Please check your internet connection.';
      } else if (error.message) {
        userMessage = error.message;
      }

      return {
        success: false,
        message: userMessage,
      };
    }
  }

  // Biometric authentication methods
  static async checkBiometricAvailability() {
    try {
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();
      
      let biometricType = 'Biometric';
      if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
        biometricType = 'Face ID';
      } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
        biometricType = 'Fingerprint';
      } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
        biometricType = 'Iris';
      }

      return {
        available: hasHardware && isEnrolled,
        type: biometricType,
        hasHardware,
        isEnrolled,
        supportedTypes,
      };
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      return {
        available: false,
        type: 'Biometric',
        hasHardware: false,
        isEnrolled: false,
        supportedTypes: [],
      };
    }
  }

  static async performBiometricLogin() {
    try {
      const biometricInfo = await this.checkBiometricAvailability();
      
      if (!biometricInfo.available) {
        throw new Error('Biometric authentication not available');
      }

      // Check for saved credentials
      const savedEmail = await AsyncStorage.getItem(this.STORAGE_KEYS.BIOMETRIC_EMAIL);
      const savedPassword = await AsyncStorage.getItem(this.STORAGE_KEYS.BIOMETRIC_PASSWORD);
      
      if (!savedEmail || !savedPassword) {
        throw new Error('No saved credentials found for biometric login');
      }

      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: `Login with ${biometricInfo.type}`,
        fallbackLabel: 'Enter your password',
        disableDeviceFallback: false,
      });

      if (result.success) {
        // Perform login with saved credentials
        const loginResult = await this.performLogin({
          email: savedEmail,
          password: savedPassword,
        }, 'biometric');

        return loginResult;
      } else {
        throw new Error('Biometric authentication failed');
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      return {
        success: false,
        message: error.message || 'Biometric login failed',
      };
    }
  }

  // Save biometric credentials
  static async saveBiometricCredentials(email, password) {
    try {
      await AsyncStorage.setItem(this.STORAGE_KEYS.BIOMETRIC_EMAIL, email);
      await AsyncStorage.setItem(this.STORAGE_KEYS.BIOMETRIC_PASSWORD, password);
      await AsyncStorage.setItem(this.STORAGE_KEYS.BIOMETRIC_USER_TYPE, 'business');
    } catch (error) {
      console.error('Error saving biometric credentials:', error);
    }
  }

  // Check for saved biometric credentials
  static async hasSavedBiometricCredentials() {
    try {
      const savedEmail = await AsyncStorage.getItem(this.STORAGE_KEYS.BIOMETRIC_EMAIL);
      const savedPassword = await AsyncStorage.getItem(this.STORAGE_KEYS.BIOMETRIC_PASSWORD);
      return !!(savedEmail && savedPassword);
    } catch (error) {
      console.error('Error checking saved credentials:', error);
      return false;
    }
  }

  // Save login preferences
  static async saveLoginPreferences(email, rememberMe, company, role, loginMethod) {
    try {
      if (rememberMe) {
        await AsyncStorage.setItem(this.STORAGE_KEYS.SAVED_EMAIL, email);
        await AsyncStorage.setItem(this.STORAGE_KEYS.REMEMBER_LOGIN, 'true');
      } else {
        await AsyncStorage.removeItem(this.STORAGE_KEYS.SAVED_EMAIL);
        await AsyncStorage.removeItem(this.STORAGE_KEYS.REMEMBER_LOGIN);
      }

      if (company) {
        await AsyncStorage.setItem(this.STORAGE_KEYS.SELECTED_COMPANY, JSON.stringify(company));
      }

      if (role) {
        await AsyncStorage.setItem(this.STORAGE_KEYS.SELECTED_ROLE, role);
      }

      if (loginMethod) {
        await AsyncStorage.setItem(this.STORAGE_KEYS.LOGIN_METHOD, loginMethod);
      }
    } catch (error) {
      console.error('Error saving login preferences:', error);
    }
  }

  // Load login preferences
  static async loadLoginPreferences() {
    try {
      const savedEmail = await AsyncStorage.getItem(this.STORAGE_KEYS.SAVED_EMAIL);
      const rememberLogin = await AsyncStorage.getItem(this.STORAGE_KEYS.REMEMBER_LOGIN);
      const selectedCompany = await AsyncStorage.getItem(this.STORAGE_KEYS.SELECTED_COMPANY);
      const selectedRole = await AsyncStorage.getItem(this.STORAGE_KEYS.SELECTED_ROLE);
      const loginMethod = await AsyncStorage.getItem(this.STORAGE_KEYS.LOGIN_METHOD);
      const recentLogins = await AsyncStorage.getItem(this.STORAGE_KEYS.RECENT_LOGINS);

      return {
        savedEmail: savedEmail || '',
        rememberLogin: rememberLogin === 'true',
        selectedCompany: selectedCompany ? JSON.parse(selectedCompany) : null,
        selectedRole: selectedRole || '',
        loginMethod: loginMethod || 'email',
        recentLogins: recentLogins ? JSON.parse(recentLogins) : [],
      };
    } catch (error) {
      console.error('Error loading login preferences:', error);
      return {
        savedEmail: '',
        rememberLogin: false,
        selectedCompany: null,
        selectedRole: '',
        loginMethod: 'email',
        recentLogins: [],
      };
    }
  }

  // Update recent logins
  static async updateRecentLogins(userInfo, company, role) {
    try {
      const recentLogins = await AsyncStorage.getItem(this.STORAGE_KEYS.RECENT_LOGINS);
      let loginsList = recentLogins ? JSON.parse(recentLogins) : [];

      const newLogin = {
        id: Date.now(),
        email: userInfo.email,
        name: userInfo.full_name || userInfo.name || 'User',
        role: role || userInfo.user_role || 'employee',
        company: company?.name || userInfo.company_name || 'Unknown Company',
        avatar: userInfo.avatar || null,
        last_login: 'Now',
      };

      // Remove existing login with same email and add new one at the beginning
      loginsList = [
        newLogin,
        ...loginsList.filter(login => login.email !== userInfo.email).slice(0, 4)
      ];

      await AsyncStorage.setItem(this.STORAGE_KEYS.RECENT_LOGINS, JSON.stringify(loginsList));
      return loginsList;
    } catch (error) {
      console.error('Error updating recent logins:', error);
      return [];
    }
  }

  // Password reset
  static async requestPasswordReset(email) {
    try {
      const response = await this.makeAuthRequest('/auth/forgot-password', {
        email: email,
        user_type: 'business',
      });

      if (response.success) {
        return {
          success: true,
          message: 'Password reset link sent to your email',
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to send reset link',
        };
      }
    } catch (error) {
      console.error('Password reset error:', error);
      return {
        success: false,
        message: 'Failed to send reset link. Please try again.',
      };
    }
  }

  // Logout
  static async performLogout() {
    try {
      // Clear all authentication data
      await AsyncStorage.multiRemove([
        this.STORAGE_KEYS.AUTH_TOKEN,
        this.STORAGE_KEYS.REFRESH_TOKEN,
        this.STORAGE_KEYS.USER_DATA,
        this.STORAGE_KEYS.COMPANY_DATA,
      ]);

      return {
        success: true,
        message: 'Logged out successfully',
      };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        message: 'Logout failed',
      };
    }
  }

  // Check authentication status
  static async checkAuthStatus() {
    try {
      const token = await AsyncStorage.getItem(this.STORAGE_KEYS.AUTH_TOKEN);
      const userData = await AsyncStorage.getItem(this.STORAGE_KEYS.USER_DATA);

      if (token && userData) {
        return {
          isAuthenticated: true,
          user: JSON.parse(userData),
          token: token,
        };
      } else {
        return {
          isAuthenticated: false,
          user: null,
          token: null,
        };
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      return {
        isAuthenticated: false,
        user: null,
        token: null,
      };
    }
  }

  // Shared navigation helpers
  static getNavigationTarget(userRole, isMultiUser = false) {
    // Determine which screen to navigate to based on user role and login type
    if (isMultiUser) {
      return 'BusinessDashboardScreen'; // Multi-user always goes to main dashboard
    } else {
      // Single user login goes to welcome screen first
      return 'WelcomeBusinessScreen';
    }
  }

  // Shared error handling
  static handleAuthError(error, context = 'Authentication') {
    console.error(`${context} error:`, error);
    
    if (error.message.includes('Network request failed')) {
      return 'Network error. Please check your internet connection.';
    } else if (error.message.includes('timeout')) {
      return 'Request timeout. Please try again.';
    } else if (error.message) {
      return error.message;
    } else {
      return `${context} failed. Please try again.`;
    }
  }

  // Shared form validation
  static validateLoginForm(formData, loginMethod = 'email') {
    const errors = [];

    switch (loginMethod) {
      case 'email':
        if (!formData.email) {
          errors.push('Email is required');
        } else if (!this.validateEmail(formData.email)) {
          errors.push('Please enter a valid email address');
        }
        break;
      case 'phone':
        if (!formData.phone) {
          errors.push('Phone number is required');
        } else if (!this.validatePhone(formData.phone)) {
          errors.push('Please enter a valid phone number');
        }
        break;
      case 'username':
        if (!formData.username) {
          errors.push('Username is required');
        } else if (!this.validateUsername(formData.username)) {
          errors.push('Username must be at least 3 characters and contain only letters, numbers, and underscores');
        }
        break;
    }

    if (!formData.password) {
      errors.push('Password is required');
    } else if (!this.validatePassword(formData.password)) {
      errors.push('Password must be at least 6 characters long');
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  static validateSignupForm(formData) {
    const errors = [];

    if (!formData.full_name || formData.full_name.trim().length < 2) {
      errors.push('Full name must be at least 2 characters long');
    }

    if (!formData.company_name || formData.company_name.trim().length < 2) {
      errors.push('Company name must be at least 2 characters long');
    }

    if (!formData.email) {
      errors.push('Email is required');
    } else if (!this.validateEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!formData.industry || formData.industry.trim().length < 2) {
      errors.push('Industry is required');
    }

    if (!formData.company_size || formData.company_size.trim().length < 1) {
      errors.push('Company size is required');
    }

    if (!formData.password) {
      errors.push('Password is required');
    } else if (!this.validatePassword(formData.password)) {
      errors.push('Password must be at least 6 characters long');
    }

    if (formData.password !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }
}

// Shared constants and configurations
export const BusinessAuthConfig = {
  // Company data for multi-user login
  COMPANIES: [
    {
      id: 1,
      name: 'JOBENG Construction Ltd',
      industry: 'Construction & Engineering',
      logo: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=100&h=100&fit=crop',
      employees: 85,
      established: 2015,
      verified: true,
      subscription: 'Premium',
    },
    {
      id: 2,
      name: 'BuildTech Solutions',
      industry: 'Construction Technology',
      logo: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=100&h=100&fit=crop',
      employees: 45,
      established: 2018,
      verified: true,
      subscription: 'Standard',
    },
    {
      id: 3,
      name: 'Urban Development Corp',
      industry: 'Real Estate Development',
      logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop',
      employees: 120,
      established: 2012,
      verified: true,
      subscription: 'Enterprise',
    },
  ],

  // User roles for multi-user system
  USER_ROLES: [
    {
      id: 'admin',
      title: 'Administrator',
      description: 'Full access to all company features and settings',
      icon: 'shield-checkmark',
      color: '#E91E63',
      permissions: ['all'],
    },
    {
      id: 'manager',
      title: 'Manager',
      description: 'Team management and project oversight',
      icon: 'people',
      color: '#2196F3',
      permissions: ['team_management', 'project_oversight', 'analytics'],
    },
    {
      id: 'employee',
      title: 'Employee',
      description: 'Access to assigned projects and personal dashboard',
      icon: 'person',
      color: '#4CAF50',
      permissions: ['personal_dashboard', 'assigned_projects'],
    },
    {
      id: 'hr',
      title: 'HR Manager',
      description: 'Recruitment and human resources management',
      icon: 'briefcase',
      color: '#FF9800',
      permissions: ['recruitment', 'hr_management', 'employee_records'],
    },
    {
      id: 'finance',
      title: 'Finance Manager',
      description: 'Financial management and reporting',
      icon: 'card',
      color: '#9C27B0',
      permissions: ['financial_management', 'invoicing', 'reporting'],
    },
    {
      id: 'project_manager',
      title: 'Project Manager',
      description: 'Project planning and execution oversight',
      icon: 'folder',
      color: '#607D8B',
      permissions: ['project_management', 'team_coordination', 'client_communication'],
    },
  ],

  // Shared styling constants
  COLORS: {
    PRIMARY: '#4A90E2',
    PRIMARY_DARK: '#357ABD',
    SECONDARY: '#10b981',
    SUCCESS: '#4CAF50',
    WARNING: '#FF9800',
    ERROR: '#E91E63',
    INFO: '#2196F3',
    BACKGROUND: '#F8FAFC',
    CARD_BACKGROUND: '#FFFFFF',
    TEXT_PRIMARY: '#0F172A',
    TEXT_SECONDARY: '#64748B',
    TEXT_MUTED: '#94A3B8',
    BORDER: '#E2E8F0',
    BORDER_FOCUSED: '#4A90E2',
  },

  // Login methods
  LOGIN_METHODS: [
    { id: 'email', title: 'Email', icon: 'mail' },
    { id: 'phone', title: 'Phone', icon: 'call' },
    { id: 'username', title: 'Username', icon: 'person' },
  ],
};

export default BusinessAuthenticationCoordinator;
